import type { Widen } from '../catalogue';
import type { svSettings } from '../sv/settings';

// Settings (SystemAdminPage and its blocks), English (OQ-162).
export const enSettings: Widen<typeof svSettings> = {
  page: {
    title: 'Settings',
    back: 'Back',
    orgsLoadFailed: 'Organisations could not be loaded'
  },
  shared: {
    name: 'Name',
    urlId: 'URL ID',
    urlIdHelp:
      'The URL ID is used when creating public access links to content belonging to an organisation, e.g. as a part of the file download URL.',
    importCsv: 'Import CSV',
    exportCsv: 'Export CSV',
    csvIncorrect: 'CSV is incorrectly formatted',
    importSuccessful: 'Import successful',
    importFailed: 'CSV failed to import',
    importPartial_one: 'Import partially successful, but line {{lines}} failed to import.',
    importPartial_other: 'Import partially successful, but lines {{lines}} failed to import.',
    orgNamePlaceholder: 'Enter a name for the organisation…',
    displayNamePlaceholder: 'Enter a new display name…',
    updateOrgInfo: 'Edit the organisation',
    logo: 'Logo',
    logoUploaded: 'Logo uploaded',
    logoUploadFailed: 'Could not upload the logo.'
  },
  roles: {
    USER: 'USER',
    ADMIN: 'ADMIN',
    SUPERADMIN: 'SUPERADMIN'
  },
  accountStatus: {
    active: 'active',
    inactive: 'inactive',
    pending: 'pending'
  },
  userBlock: {
    title: 'My profile',
    changePassword: 'Change Password',
    editTitle: 'Change your name',
    updateError: 'Error updating user: {{error}}',
    updateFailed: 'Could not save the name.',
    passwordTitle: 'Change user password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    repeatPassword: 'Repeat password',
    wrongPassword: 'Current password was incorrect',
    submitPassword: 'Change password',
    editName: 'Change name',
    saved: 'Name saved',
    passwordChanged: 'Password changed',
    passwordFailed: 'Could not change the password.'
  },
  organisationBlock: {
    title: 'Organisation',
    requireLocation: 'Require location on upload',
    requireLocationHelp: 'When it is off, photos can be sent through camera links without a position.',
    updateError: 'Error updating user: {{error}}',
    updateFailed: 'Could not save the organisation.',
    saved: 'Organisation saved'
  },
  orgColor: {
    label: 'Color',
    help: 'Click colorbox to choose color or type in hexcode below',
    placeholder: 'Enter a color for the organisation',
    invalid: "Organisation's color hexcode is not correctly formatted"
  },
  generalBlock: {
    title: 'General',
    editOrg: 'Edit organisation',
    created: 'Created',
    urlIdPlaceholder: 'Enter a unique URL id…',
    urlIdNotUnique: "Organisation's URL ID is not unique",
    urlIdBadFormat: "Organisation's URL ID is incorrectly formatted",
    updateError: 'Error updating organisation: {{error}}',
    updateFailed: 'Could not save the organisation.'
  },
  accountBlock: {
    title: 'Users',
    addAccount: 'Add user',
    adminLocked: 'Only the system administrator can change administrators',
    orgIdNotFound: 'Organisation ID could not be found.',
    columnEmail: 'Email',
    columnRole: 'Role',
    columnStatus: 'Status',
    loadFailed: 'Accounts could not be loaded.',
    empty: 'The organisation has no users yet.',
    activateTitle: 'Activate the account?',
    deactivateTitle: 'Deactivate the account?',
    resendConfirm: 'Are you sure that you want to resend a activation link to this account?',
    sendEmail: 'Send',
    activateConfirm: 'Are you sure you want to activate this account?',
    deactivateConfirm: 'Are you sure you want to deactivate this account?',
    activate: 'Activate',
    deactivate: 'Deactivate',
    deleteTitle: 'Delete the user?',
    deleteConfirm: '{{name}} is deleted and can no longer sign in. This cannot be undone.',
    deleteError: 'Error adding user: {{error}}',
    deleteFailed: 'Could not delete the user.',
    createTitle: 'Add user',
    editTitle: 'Edit user',
    email: 'Email',
    emailPlaceholder: 'Enter the user’s e-mail address…',
    role: 'Role',
    saveError: 'Error adding user: {{error}}',
    unknownError: 'Unknown error',
    saveFailed: 'Failed to add user: {{error}}',
    addUser: 'Add user',
    resendTitle: 'Send the activation link again?',
    resent: 'A new activation link was sent to {{email}}',
    resendFailed: 'Could not send the activation link.',
    activated: '{{name}} activated',
    deactivated: '{{name}} deactivated',
    statusFailed: 'Could not change the account status.',
    deleteButton: 'Delete the user',
    deleted: '{{name}} deleted',
    created: '{{email}} added and sent an activation link',
    saved: '{{email}} saved'
  },
  statusBlock: {
    title: 'Statuses',
    newStatus: 'Add status',
    columnText: 'Text',
    columnIndex: 'Order Index',
    empty: 'The organisation has no statuses yet.',
    deleteTitle: 'Delete the status?',
    deleteConfirm: 'The status "{{name}}" is deleted. This cannot be undone.',
    deleteError: 'Error adding user: {{error}}',
    deleteFailed: 'Could not delete the status.',
    created: 'Status {{text}} added',
    saved: 'Status {{text}} saved',
    createError: 'Error creating status: {{error}}',
    updateError: 'Error updating status: {{error}}',
    createFailed: 'Could not add the status.',
    updateFailed: 'Could not save the status.',
    createTitle: 'Add status',
    editTitle: 'Edit status',
    namePlaceholder: 'Enter a name for the status…',
    text: 'Text',
    textPlaceholder: 'Enter the text shown for the status…',
    sortIndex: 'Sort Index',
    addStatus: 'Add status',
    deleteButton: 'Delete the status',
    deleted: 'Status {{name}} deleted'
  },
  organisationsBlock: {
    title: 'Organisations',
    addOrg: 'Add organisation',
    columnLinkId: 'Link ID',
    empty: 'There are no organisations yet.',
    verified: 'Successfully verified account: {{email}}!',
    wrongPassword: 'Wrong password!',
    deleteTitle: 'Delete the organisation?',
    orgName: 'Organisation name:',
    orgId: 'Organisation ID:',
    verifyLabel: 'You must enter your password and verify your account to perform this action',
    passwordPlaceholder: 'Enter your password…',
    accountVerified: 'Account verified',
    verifyAccount: 'Verify account',
    permanentWarning: 'This action is <strong><u>permanent</u>, and will delete all data</strong>',
    deleteError: 'Error adding user: {{error}}',
    deleteFailed: 'Could not delete the organisation.',
    deleteOrg: 'Delete organisation',
    createTitle: 'Add organisation',
    linkIdPlaceholder: 'Enter a unique link id…',
    linkIdNotUnique: "Organisation's link ID is not unique",
    linkIdBadFormat: "Organisation's link ID is incorrectly formatted",
    createError: 'Error adding user: {{error}}',
    createFailed: 'Could not add the organisation.',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    deleted: 'Organisation {{name}} deleted',
    created: 'Organisation {{name}} added'
  },
  backupBlock: {
    title: 'Backups',
    columnDate: 'Date',
    empty: 'No backups have been made yet. Backups are created at 01:00 every night.',
    restoreTitle: 'Restore the backup?',
    restoreConfirm: 'Are you sure you want to restore the backup created on <strong>{{date}}</strong>?',
    restoreError: 'Error restoring backup: {{error}}',
    restoreFailed: 'Could not restore the backup.',
    restoreExplain:
      'The media items in the backup are put back and overwrite changes made to them since. Media items added after {{date}} stay.',
    restoreButton: 'Restore',
    restored: 'Backup from {{date}} restored'
  }
};
